import { type AddTaskProps } from '@/components/Modals/AddTask/AddTask.model';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { Study } from '@/model/Study.model';
import { type TaskSpec, type TaskSpecApiId, type TaskSpecUpdate } from '@/model/Task.model';
import type { TreatmentV1 } from '@/model/Treatment.model';
import { ApiService } from '@/support/ApiService';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useModalAction } from '@/utils/modal';
import { useState } from 'react';

interface UseUpdateTaskModalProps {
  study?: Study;
  onSaved?: () => void;
}

const glossaryError = { toast: false, capture: true, throw: false } as const;

const measurementOptions = (
  calculations: Array<PresetCalculation> | undefined
): NonNullable<AddTaskProps['execution']>['measurements'] =>
  (calculations ?? [])
    .reduce<Array<{ id: string; title: string }>>((options, calculation) => {
      if ((calculation.measurements?.length ?? 0) > 0) {
        options.push({ id: calculation.id, title: calculation.name });
      }
      return options;
    }, [])
    .sort((left, right) => left.title.localeCompare(right.title));

const titledOptions = (rows: Array<{ api_id: string; title?: string }>): Array<{ id: string; title: string }> =>
  rows
    .map(({ api_id: id, title }) => ({ id, title: title ?? '' }))
    .sort((left, right) => left.title.localeCompare(right.title));

/**
 * Loads the full task spec and the same option lists Settings uses, then opens
 * the existing Update task modal.
 */
export const useUpdateTaskModal = ({ study, onSaved }: UseUpdateTaskModalProps) => {
  const { openModal } = useModalAction();
  const isMounted = useMountedState();
  const [loading, setLoading] = useState<boolean>(false);

  const openUpdateTask = async (taskSpecApiId: TaskSpecApiId): Promise<void> => {
    if (!_notNil(study)) {
      return;
    }

    setLoading(true);
    const studyApiId = study.api_id;
    try {
      const [taskSpecResponse, samplesResponse, observationsResponse, treatmentsResponse] = await Promise.all([
        ApiService.call({
          endpoint: 'GET /api/v1/studies/{studyId}/task-specs/{taskSpecId}',
          path: { studyId: studyApiId, taskSpecId: taskSpecApiId },
          options: { onError: { toast: false, capture: true, throw: false, slug: 'overview-task-spec-load' } },
        }),
        ApiService.call({
          endpoint: 'GET /api/v1/team-glossary',
          path: undefined,
          query: {
            group: 'samples',
            perPage: -1,
            filterType: 'and',
            'filters[]': ['status|eq|active'],
            order: 'asc',
          },
          options: { onError: { ...glossaryError, slug: 'samples-glossary-load' } },
        }),
        ApiService.call({
          endpoint: 'GET /api/v1/team-glossary',
          path: undefined,
          query: {
            group: 'observations',
            perPage: -1,
            filterType: 'and',
            'filters[]': ['status|eq|active'],
            order: 'asc',
          },
          options: { onError: { ...glossaryError, slug: 'observations-glossary-load' } },
        }),
        ApiService.call({
          endpoint: 'GET /api/v1/studies/{studyApiId}/treatments',
          path: { studyApiId },
          query: { perPage: -1 },
          options: { onError: { ...glossaryError, slug: 'study-treatments-load' } },
        }),
      ]);

      if (!isMounted()) {
        return;
      }

      if (
        taskSpecResponse.type !== 'success' ||
        samplesResponse.type !== 'success' ||
        observationsResponse.type !== 'success' ||
        treatmentsResponse.type !== 'success'
      ) {
        errorToast('Could not load task');
        return;
      }

      const samples = (samplesResponse.body as { data?: Array<TeamGlossaryV1> }).data ?? [];
      const observations = (observationsResponse.body as { data?: Array<TeamGlossaryV1> }).data ?? [];
      const treatments = (treatmentsResponse.body as { data?: Array<TreatmentV1> }).data ?? [];
      const execution: AddTaskProps['execution'] = {
        measurements: measurementOptions(study.settings?.calculations),
        observations: titledOptions(observations),
        samples: titledOptions(samples),
        treatments: treatments
          .map(({ api_id: id, treatment_label: title }) => ({ id, title }))
          .sort((left, right) => left.title.localeCompare(right.title)),
      };

      openModal('EDIT_TASK', {
        studyApiId,
        studyId: study.id,
        trackingDayBase: study.settings?.tracking_day_base,
        taskSpec: taskSpecResponse.body as TaskSpec,
        execution,
        disabledSections: ['target'],
        handleSubmit: async (taskSpec: TaskSpecUpdate) => {
          const update = await ApiService.call({
            endpoint: 'PATCH /api/v1/studies/{studyId}/task-specs/{taskSpecId}',
            body: taskSpec,
            path: { studyId: studyApiId, taskSpecId: taskSpecApiId },
            options: { onError: { toast: false, capture: true, throw: false, slug: 'overview-task-spec-update' } },
          });
          if (update.type !== 'success') {
            errorToast('Could not update task');
            throw new Error('Could not update task');
          }
          successToast('Successfully updated task');
          onSaved?.();
        },
      });
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  return { loading, openUpdateTask };
};
